import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  FaTasks, FaPlus, FaSearch, FaFilter, FaTimes, FaCheck, FaClock,
  FaExclamationCircle, FaChevronLeft, FaChevronRight, FaEdit, FaTrash,
  FaUser, FaCalendarAlt, FaSortAmountDown, FaSortAmountUp, FaEye
} from 'react-icons/fa';
import {
  collection, getDocs, addDoc, updateDoc, deleteDoc, doc,
  query, orderBy, serverTimestamp, Timestamp
} from 'firebase/firestore';
import { db } from '../../config/firebaseConfig';
import styles from './TodoListPage.module.css';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useTheme } from '../../contexts/ThemeContext';
import { useUserProfile } from '../../contexts/UserProfileContext';
import AddTaskModal from './AddTaskModal';
import TaskDetailModal from './TaskDetailModal';

const PRIORITY_CONFIG = {
  high: { label: 'Yüksek', color: '#e53e3e', bgColor: '#fff5f5', icon: <FaExclamationCircle /> },
  medium: { label: 'Orta', color: '#dd6b20', bgColor: '#fffaf0', icon: <FaClock /> },
  low: { label: 'Düşük', color: '#38a169', bgColor: '#f0fff4', icon: <FaCheck /> }
};

const STATUS_CONFIG = {
  pending: { label: 'Beklemede', color: '#718096', bgColor: '#f7fafc' },
  'in-progress': { label: 'Devam Ediyor', color: '#3182ce', bgColor: '#ebf8ff' },
  completed: { label: 'Tamamlandı', color: '#38a169', bgColor: '#f0fff4' },
  cancelled: { label: 'İptal Edildi', color: '#e53e3e', bgColor: '#fff5f5' }
};

const PAGE_SIZE = 20;

const TodoListPage = () => {
  const { isDarkMode } = useTheme();
  const { userAuthorization, employeeData, employeeId } = useUserProfile();

  const isAdmin = userAuthorization?.includes('admin') || userAuthorization?.includes('Kısıtlı Admin');

  // Data
  const [tasks, setTasks] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // UI State
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [assigneeFilter, setAssigneeFilter] = useState('all');
  const [sortConfig, setSortConfig] = useState({ key: 'createdAt', direction: 'desc' });
  const [currentPage, setCurrentPage] = useState(1);
  const [showFilters, setShowFilters] = useState(false);

  // Modals
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [selectedTask, setSelectedTask] = useState(null);

  // Fetch tasks
  const fetchTasks = useCallback(async () => {
    try {
      setIsLoading(true);
      const tasksRef = collection(db, 'Tasks');
      const q = query(tasksRef, orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(q);
      const tasksList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setTasks(tasksList);
    } catch (error) {
      console.error('Error fetching tasks:', error);
      toast.error('Görevler yüklenirken hata oluştu');
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Fetch employees for assignment dropdown
  const fetchEmployees = useCallback(async () => {
    try {
      const empRef = collection(db, 'Employees');
      const q = query(empRef, orderBy('eName', 'asc'));
      const snapshot = await getDocs(q);
      const empList = snapshot.docs
        .filter(doc => doc.data().isActive !== false)
        .map(doc => ({
          id: doc.id,
          eName: doc.data().eName || '',
          eSurname: doc.data().eSurname || '',
          eMail: doc.data().eMail || ''
        }));
      setEmployees(empList);
    } catch (error) {
      console.error('Error fetching employees:', error);
    }
  }, []);

  // Özgeçmiş güncelleme görevlerini kontrol et ve oluştur
  const checkAndCreateCVUpdateTasks = useCallback(async () => {
    try {
      const today = new Date();
      const threeMonthsAgo = new Date(today);
      threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3);

      // Tüm çalışanları getir
      const employeesRef = collection(db, 'Employees');
      const employeesSnapshot = await getDocs(employeesRef);
      const allEmployees = employeesSnapshot.docs
        .filter(doc => {
          const data = doc.data();
          // Sadece aktif çalışanlar için görev oluştur
          return data.Active === 1 || data.Active === '1' || data.state === 'aktif';
        })
        .map(doc => ({
          id: doc.id,
          ...doc.data()
        }));

      console.log(`${allEmployees.length} aktif çalışan bulundu, özgeçmiş görevleri kontrol ediliyor...`);

      for (const employee of allEmployees) {
        const employeeName = `${employee.eName || ''} ${employee.eSurname || ''}`.trim();
        
        // Bu çalışan için özgeçmiş güncelleme görevi var mı kontrol et
        const tasksRef = collection(db, 'Tasks');
        const q = query(tasksRef);
        const tasksSnapshot = await getDocs(q);
        
        // Bu çalışana ait özgeçmiş güncelleme görevlerini bul
        const existingCVTasks = tasksSnapshot.docs
          .map(doc => ({ id: doc.id, ...doc.data() }))
          .filter(task => 
            task.isCVUpdateTask === true &&
            task.assigneeId === employee.id &&
            task.status !== 'completed' &&
            task.status !== 'cancelled'
          );

        // Eğer aktif bir özgeçmiş güncelleme görevi yoksa, yeni oluştur
        if (existingCVTasks.length === 0) {
          // Son tamamlanan özgeçmiş güncelleme görevini kontrol et
          const completedCVTasks = tasksSnapshot.docs
            .map(doc => ({ id: doc.id, ...doc.data() }))
            .filter(task => 
              task.isCVUpdateTask === true &&
              task.assigneeId === employee.id &&
              task.status === 'completed'
            )
            .sort((a, b) => {
              const aDate = a.completedAt?.toDate?.() || new Date(0);
              const bDate = b.completedAt?.toDate?.() || new Date(0);
              return bDate - aDate;
            });

          let shouldCreateTask = false;

          if (completedCVTasks.length === 0) {
            // Hiç özgeçmiş güncelleme görevi tamamlanmamış, yeni görev oluştur
            shouldCreateTask = true;
          } else {
            // Son tamamlanan görev 3 aydan eski mi kontrol et
            const lastCompletedTask = completedCVTasks[0];
            const lastCompletedDate = lastCompletedTask.completedAt?.toDate?.() || new Date(0);
            
            if (lastCompletedDate < threeMonthsAgo) {
              shouldCreateTask = true;
            }
          }

          if (shouldCreateTask) {
            // Yeni özgeçmiş güncelleme görevi oluştur
            const dueDate = new Date(today);
            dueDate.setDate(dueDate.getDate() + 7); // 1 hafta içinde tamamlanmalı

            const newTask = {
              title: 'Özgeçmiş Güncelleme',
              description: `Lütfen özgeçmişinizi güncelleyiniz. Özgeçmişinizde son 3 aydaki değişiklikleri (yeni projeler, eğitimler, sertifikalar vb.) eklemeyi unutmayınız.`,
              priority: 'medium',
              status: 'pending',
              assigneeId: employee.id,
              assigneeName: employeeName,
              assignees: [{
                id: employee.id,
                name: employeeName,
                email: employee.eMail || ''
              }],
              dueDate: Timestamp.fromDate(dueDate),
              createdAt: serverTimestamp(),
              updatedAt: serverTimestamp(),
              isCVUpdateTask: true, // Özgeçmiş güncelleme görevi olduğunu işaretle
              autoCreated: true, // Otomatik oluşturulduğunu işaretle
              createdBy: 'system' // Sistem tarafından oluşturuldu
            };

            await addDoc(collection(db, 'Tasks'), newTask);
            console.log(`✅ ${employeeName} için özgeçmiş güncelleme görevi oluşturuldu`);
          }
        }
      }

      console.log('Özgeçmiş güncelleme görevleri kontrolü tamamlandı');
    } catch (error) {
      console.error('Özgeçmiş güncelleme görevleri oluşturulurken hata:', error);
    }
  }, []);

  useEffect(() => {
    fetchTasks();
    fetchEmployees();
    
    // Sayfa yüklendiğinde özgeçmiş güncelleme görevlerini kontrol et
    checkAndCreateCVUpdateTasks();
  }, [fetchTasks, fetchEmployees, checkAndCreateCVUpdateTasks]);

  // Filter & sort tasks
  const visibleTasks = useMemo(() => {
    let filtered = [...tasks];

    console.log('=== GÖREV FİLTRELEME DEBUG ===');
    console.log('Toplam görev sayısı:', tasks.length);
    console.log('Mevcut kullanıcı ID:', employeeId);
    console.log('Kullanıcı admin mi:', isAdmin);

    // Show all tasks to everyone - no filtering by user
    console.log('Tüm görevler gösteriliyor, filtreleme yok');

    // Search
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(task =>
        (task.title || '').toLowerCase().includes(term) ||
        (task.description || '').toLowerCase().includes(term) ||
        (task.assigneeName || '').toLowerCase().includes(term) ||
        (task.assignees || []).some(a => (a.name || '').toLowerCase().includes(term))
      );
    }

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(task => task.status === statusFilter);
    }

    // Priority filter
    if (priorityFilter !== 'all') {
      filtered = filtered.filter(task => task.priority === priorityFilter);
    }

    // Assignee filter
    if (assigneeFilter !== 'all') {
      filtered = filtered.filter(task =>
        task.assigneeId === assigneeFilter ||
        (task.assignees && task.assignees.some(a => a.id === assigneeFilter))
      );
    }

    // Sort
    if (sortConfig.key) {
      filtered.sort((a, b) => {
        let aVal = a[sortConfig.key];
        let bVal = b[sortConfig.key];

        // Handle Timestamp
        if (aVal?.toDate) aVal = aVal.toDate();
        if (bVal?.toDate) bVal = bVal.toDate();

        if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }

    return filtered;
  }, [tasks, searchTerm, statusFilter, priorityFilter, assigneeFilter, sortConfig]);

  // Pagination
  const totalPages = Math.ceil(visibleTasks.length / PAGE_SIZE);
  const paginatedTasks = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    return visibleTasks.slice(start, start + PAGE_SIZE);
  }, [visibleTasks, currentPage]);

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter, priorityFilter, assigneeFilter]);

  // Stats - Show stats for all tasks
  const stats = useMemo(() => {
    // Show stats for all tasks, not filtered by user
    const base = tasks;
    return {
      total: base.length,
      pending: base.filter(t => t.status === 'pending').length,
      inProgress: base.filter(t => t.status === 'in-progress').length,
      completed: base.filter(t => t.status === 'completed').length,
      cancelled: base.filter(t => t.status === 'cancelled').length
    };
  }, [tasks]);

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handleStatusChange = async (taskId, newStatus) => {
    try {
      // Önce mevcut görevi al
      const currentTask = tasks.find(t => t.id === taskId);
      if (!currentTask) {
        toast.error('Görev bulunamadı');
        return;
      }

      // Muhasebe ekibi üyeleri
      const accountingTeam = ['AYGÜL DAĞ CEYLAN', 'ÖZGE YIKIMLAR', 'MELİSA ŞENER'];
      
      // Bu görevin muhasebe ekibi görevi olup olmadığını kontrol et
      const isAccountingTask = currentTask.isAccountingTask || 
                              currentTask.assignees?.some(assignee => 
                                accountingTeam.some(member => 
                                  assignee.name?.toUpperCase().includes(member.toUpperCase())
                                )
                              );

      // Eğer muhasebe görevi ise ve tamamlandı durumuna geçiyorsa
      if (isAccountingTask && newStatus === 'completed') {
        let relatedAccountingTasks = [];

        // Yeni grup sistemi varsa accountingGroup ile bul
        if (currentTask.accountingGroup) {
          relatedAccountingTasks = tasks.filter(task => 
            task.accountingGroup === currentTask.accountingGroup
          );
        } else {
          // Eski sistem: aynı başlık ve açıklamaya sahip muhasebe görevlerini bul
          relatedAccountingTasks = tasks.filter(task => {
            if (task.id === taskId) return true; // Mevcut görevi dahil et
            
            // Aynı başlık kontrolü (muhasebe işlemleri kısmını çıkar)
            const currentTitle = currentTask.title.replace(' - Muhasebe İşlemleri', '');
            const taskTitle = task.title.replace(' - Muhasebe İşlemleri', '');
            const isSameTask = taskTitle === currentTitle;
            
            // Muhasebe ekibi üyesi kontrolü
            const isAccountingMember = task.isAccountingTask || 
                                     task.assignees?.some(assignee => 
                                       accountingTeam.some(member => 
                                         assignee.name?.toUpperCase().includes(member.toUpperCase())
                                       )
                                     );
            
            return isSameTask && isAccountingMember;
          });
        }

        console.log(`Muhasebe görevi tamamlandı. ${relatedAccountingTasks.length} ilişkili görev bulundu.`);

        // Tüm ilişkili görevleri tamamlandı durumuna getir
        const updatePromises = relatedAccountingTasks.map(async (task) => {
          const taskRef = doc(db, 'Tasks', task.id);
          const updateData = {
            status: 'completed',
            updatedAt: serverTimestamp(),
            completedAt: serverTimestamp(),
            syncedBy: currentTask.id, // Hangi görev tarafından senkronize edildiğini kaydet
            syncedAt: serverTimestamp()
          };
          return updateDoc(taskRef, updateData);
        });

        await Promise.all(updatePromises);
        
        const syncedCount = relatedAccountingTasks.length - 1;
        if (syncedCount > 0) {
          toast.success(`Görev tamamlandı ve ${syncedCount} ilişkili muhasebe görevi otomatik olarak tamamlandı`);
        } else {
          toast.success('Görev tamamlandı');
        }
      } else {
        // Normal görev güncelleme
        const taskRef = doc(db, 'Tasks', taskId);
        const updateData = {
          status: newStatus,
          updatedAt: serverTimestamp()
        };
        if (newStatus === 'completed') {
          updateData.completedAt = serverTimestamp();
        }
        await updateDoc(taskRef, updateData);
        toast.success('Görev durumu güncellendi');
      }

      fetchTasks();
    } catch (error) {
      console.error('Error updating task status:', error);
      toast.error('Görev durumu güncellenirken hata oluştu');
    }
  };

  const handleDeleteTask = async (taskId) => {
    if (!window.confirm('Bu görevi silmek istediğinize emin misiniz?')) return;
    try {
      await deleteDoc(doc(db, 'Tasks', taskId));
      toast.success('Görev silindi');
      fetchTasks();
    } catch (error) {
      console.error('Error deleting task:', error);
      toast.error('Görev silinirken hata oluştu');
    }
  };

  const handleEditTask = (task) => {
    setEditingTask(task);
    setShowAddModal(true);
  };

  const handleViewTask = (task) => {
    setSelectedTask(task);
    setShowDetailModal(true);
  };

  const handleModalClose = () => {
    setShowAddModal(false);
    setEditingTask(null);
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return '-';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const isOverdue = (task) => {
    if (!task.dueDate || task.status === 'completed' || task.status === 'cancelled') return false;
    const due = task.dueDate.toDate ? task.dueDate.toDate() : new Date(task.dueDate);
    return due < new Date();
  };

  const getAssigneeNames = (task) => {
    if (task.assignees && task.assignees.length > 0) {
      const names = task.assignees.map(a => a.name);
      if (names.length <= 2) return names.join(', ');
      return `${names.slice(0, 2).join(', ')} +${names.length - 2} daha`;
    }
    return task.assigneeName || '-';
  };

  const SortIcon = ({ column }) => {
    if (sortConfig.key !== column) return null;
    return sortConfig.direction === 'asc' ? <FaSortAmountUp /> : <FaSortAmountDown />;
  };

  if (isLoading) {
    return (
      <div className={`${styles.pageContainer} ${isDarkMode ? styles.dark : ''}`}>
        <div className={styles.loadingContainer}>
          <div className={styles.spinner} />
          <p>Görevler yükleniyor...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`${styles.pageContainer} ${isDarkMode ? styles.dark : ''}`}>
      <ToastContainer position="top-right" autoClose={3000} />
      <div className={styles.container}>
        {/* Header */}
        <div className={styles.header}>
          <div className={styles.headerContent}>
            <div className={styles.headerLeft}>
              <FaTasks className={styles.headerIcon} />
              <div>
                <h1 className={styles.headerTitle}>Yapılacaklar Listesi</h1>
                <p className={styles.headerSubtitle}>
                  Tüm görevleri görüntüleyin ve takip edin
                </p>
              </div>
            </div>
            {isAdmin && (
              <button className={styles.addButton} onClick={() => setShowAddModal(true)}>
                <FaPlus />
                <span>Yeni Görev</span>
              </button>
            )}
          </div>
        </div>

        {/* Stats Cards */}
        <div className={styles.statsCards}>
          <div className={`${styles.statCard} ${styles.statTotal}`}>
            <div className={styles.statIcon}><FaTasks /></div>
            <div className={styles.statInfo}>
              <span className={styles.statNumber}>{stats.total}</span>
              <span className={styles.statLabel}>Toplam</span>
            </div>
          </div>
          <div className={`${styles.statCard} ${styles.statPending}`}>
            <div className={styles.statIcon}><FaClock /></div>
            <div className={styles.statInfo}>
              <span className={styles.statNumber}>{stats.pending}</span>
              <span className={styles.statLabel}>Beklemede</span>
            </div>
          </div>
          <div className={`${styles.statCard} ${styles.statProgress}`}>
            <div className={styles.statIcon}><FaExclamationCircle /></div>
            <div className={styles.statInfo}>
              <span className={styles.statNumber}>{stats.inProgress}</span>
              <span className={styles.statLabel}>Devam Ediyor</span>
            </div>
          </div>
          <div className={`${styles.statCard} ${styles.statCompleted}`}>
            <div className={styles.statIcon}><FaCheck /></div>
            <div className={styles.statInfo}>
              <span className={styles.statNumber}>{stats.completed}</span>
              <span className={styles.statLabel}>Tamamlandı</span>
            </div>
          </div>
        </div>

        {/* Search & Filters */}
        <div className={styles.toolbar}>
          <div className={styles.searchContainer}>
            <FaSearch className={styles.searchIcon} />
            <input
              type="text"
              placeholder="Görev ara..."
              className={styles.searchInput}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {searchTerm && (
              <button className={styles.clearSearch} onClick={() => setSearchTerm('')}>
                <FaTimes />
              </button>
            )}
          </div>
          <button
            className={`${styles.filterToggle} ${showFilters ? styles.filterActive : ''}`}
            onClick={() => setShowFilters(!showFilters)}
          >
            <FaFilter />
            <span>Filtrele</span>
          </button>
        </div>

        {showFilters && (
          <div className={styles.filtersPanel}>
            <div className={styles.filterGroup}>
              <label>Durum:</label>
              <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                <option value="all">Tümü</option>
                <option value="pending">Beklemede</option>
                <option value="in-progress">Devam Ediyor</option>
                <option value="completed">Tamamlandı</option>
                <option value="cancelled">İptal Edildi</option>
              </select>
            </div>
            <div className={styles.filterGroup}>
              <label>Öncelik:</label>
              <select value={priorityFilter} onChange={(e) => setPriorityFilter(e.target.value)}>
                <option value="all">Tümü</option>
                <option value="high">Yüksek</option>
                <option value="medium">Orta</option>
                <option value="low">Düşük</option>
              </select>
            </div>
            <div className={styles.filterGroup}>
              <label>Atanan Kişi:</label>
              <select value={assigneeFilter} onChange={(e) => setAssigneeFilter(e.target.value)}>
                <option value="all">Tümü</option>
                {employees.map(emp => (
                  <option key={emp.id} value={emp.id}>
                    {emp.eName} {emp.eSurname}
                  </option>
                ))}
              </select>
            </div>
            <button className={styles.clearFilters} onClick={() => {
              setStatusFilter('all');
              setPriorityFilter('all');
              setAssigneeFilter('all');
            }}>
              <FaTimes /> Temizle
            </button>
          </div>
        )}

        {/* Results count */}
        <div className={styles.resultsInfo}>
          <span>{visibleTasks.length} görev bulundu</span>
        </div>

        {/* Table */}
        <div className={styles.tableWrapper}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th className={styles.thSortable} onClick={() => handleSort('title')}>
                  Görev <SortIcon column="title" />
                </th>
                <th className={styles.thSortable} onClick={() => handleSort('priority')}>
                  Öncelik <SortIcon column="priority" />
                </th>
                <th className={styles.thSortable} onClick={() => handleSort('status')}>
                  Durum <SortIcon column="status" />
                </th>
                <th>Atanan</th>
                <th className={styles.thSortable} onClick={() => handleSort('dueDate')}>
                  Son Tarih <SortIcon column="dueDate" />
                </th>
                <th className={styles.thSortable} onClick={() => handleSort('createdAt')}>
                  Oluşturulma <SortIcon column="createdAt" />
                </th>
                <th>İşlemler</th>
              </tr>
            </thead>
            <tbody>
              {paginatedTasks.length === 0 ? (
                <tr>
                  <td colSpan="7" className={styles.emptyRow}>
                    <FaTasks className={styles.emptyIcon} />
                    <p>Görev bulunamadı</p>
                  </td>
                </tr>
              ) : (
                paginatedTasks.map(task => (
                  <tr
                    key={task.id}
                    className={`${styles.tableRow} ${isOverdue(task) ? styles.overdueRow : ''}`}
                  >
                    <td className={styles.titleCell}>
                      <span className={styles.taskTitle}>{task.title}</span>
                      {task.description && (
                        <span className={styles.taskDescription}>
                          {task.description.length > 60
                            ? task.description.substring(0, 60) + '...'
                            : task.description}
                        </span>
                      )}
                    </td>
                    <td>
                      <span
                        className={styles.priorityBadge}
                        style={{
                          color: PRIORITY_CONFIG[task.priority]?.color,
                          backgroundColor: isDarkMode ? 'transparent' : PRIORITY_CONFIG[task.priority]?.bgColor,
                          borderColor: PRIORITY_CONFIG[task.priority]?.color
                        }}
                      >
                        {PRIORITY_CONFIG[task.priority]?.icon}
                        {PRIORITY_CONFIG[task.priority]?.label}
                      </span>
                    </td>
                    <td>
                      {isAdmin || task.assigneeId === employeeId || (task.assignees && task.assignees.some(a => a.id === employeeId)) ? (
                        <select
                          className={styles.statusSelect}
                          value={task.status}
                          onChange={(e) => handleStatusChange(task.id, e.target.value)}
                          style={{
                            color: STATUS_CONFIG[task.status]?.color,
                            borderColor: STATUS_CONFIG[task.status]?.color
                          }}
                        >
                          {Object.entries(STATUS_CONFIG).map(([key, val]) => (
                            <option key={key} value={key}>{val.label}</option>
                          ))}
                        </select>
                      ) : (
                        <span
                          className={styles.statusBadge}
                          style={{
                            color: STATUS_CONFIG[task.status]?.color,
                            backgroundColor: isDarkMode ? 'transparent' : STATUS_CONFIG[task.status]?.bgColor,
                            borderColor: STATUS_CONFIG[task.status]?.color
                          }}
                        >
                          {STATUS_CONFIG[task.status]?.label}
                        </span>
                      )}
                    </td>
                    <td>
                      <div className={styles.assigneeCell}>
                        <FaUser className={styles.assigneeIcon} />
                        <span>{getAssigneeNames(task)}</span>
                      </div>
                    </td>
                    <td>
                      <span className={isOverdue(task) ? styles.overdueDateText : ''}>
                        {formatDate(task.dueDate)}
                      </span>
                      {isOverdue(task) && <span className={styles.overdueLabel}>Gecikmiş</span>}
                    </td>
                    <td>{formatDate(task.createdAt)}</td>
                    <td>
                      <div className={styles.actionButtons}>
                        <button
                          className={`${styles.actionBtn} ${styles.viewBtn}`}
                          onClick={() => handleViewTask(task)}
                          title="Detay"
                        >
                          <FaEye />
                        </button>
                        {isAdmin && (
                          <>
                            <button
                              className={`${styles.actionBtn} ${styles.editBtn}`}
                              onClick={() => handleEditTask(task)}
                              title="Düzenle"
                            >
                              <FaEdit />
                            </button>
                            <button
                              className={`${styles.actionBtn} ${styles.deleteBtn}`}
                              onClick={() => handleDeleteTask(task.id)}
                              title="Sil"
                            >
                              <FaTrash />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className={styles.pagination}>
            <button
              className={styles.pageBtn}
              disabled={currentPage === 1}
              onClick={() => setCurrentPage(prev => prev - 1)}
            >
              <FaChevronLeft />
            </button>
            <span className={styles.pageInfo}>
              {currentPage} / {totalPages}
            </span>
            <button
              className={styles.pageBtn}
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage(prev => prev + 1)}
            >
              <FaChevronRight />
            </button>
          </div>
        )}
      </div>

      {/* Add/Edit Task Modal */}
      {showAddModal && (
        <AddTaskModal
          onClose={handleModalClose}
          onUpdate={fetchTasks}
          employees={employees}
          editingTask={editingTask}
        />
      )}

      {/* Task Detail Modal */}
      {showDetailModal && selectedTask && (
        <TaskDetailModal
          task={selectedTask}
          onClose={() => { setShowDetailModal(false); setSelectedTask(null); }}
          onStatusChange={handleStatusChange}
          isAdmin={isAdmin}
          employeeId={employeeId}
        />
      )}
    </div>
  );
};

export default TodoListPage;
